import React, { useState, useMemo } from "react";
import { DetailsList, IColumn, DetailsListLayoutMode, IGroup, IDetailsGroupDividerProps  } from "@fluentui/react/lib/DetailsList";
import { Stack, Text } from "@fluentui/react";
import { gSyncLogItem } from "../../interfaces";
import styles from "../AnalyticsConvoList/AnalyticsConvoList.module.css";
import { controlStylesAnalyticsConvoList } from "../../styles_glob";
import syncLogStyles from "./TableSyncLog.module.css";

interface TableSyncLogProps {
    data: gSyncLogItem[];
}

interface MetricCardProps {
    title: string;
    value: string | number;
    subtitle?: string;
    icon?: string;
}

const baseColumns: IColumn[] = [
    {
        key: "date",
        name: "Date",
        fieldName: "stamp",
        minWidth: 120,
        maxWidth: 180,
        isResizable: true,
        onRender: (item: gSyncLogItem) => new Date(item.stamp).toLocaleString()
    },
    {
        key: "tid",
        name: "Job ID",
        fieldName: "tid",
        minWidth: 80,
        maxWidth: 120,
        isResizable: true
    },
    {
        key: "fid",
        name: "File ID",
        fieldName: "fid",
        minWidth: 60,
        maxWidth: 100,
        isResizable: true
    },
    {
        key: "filename",
        name: "File Name",
        fieldName: "filename",
        minWidth: 120,
        maxWidth: 200,
        isResizable: true
    },
    {
        key: "groupname",
        name: "Group Name",
        fieldName: "groupname",
        minWidth: 120,
        maxWidth: 200,
        isResizable: true
    },
    {
        key: "page_count",
        name: "Page Count",
        fieldName: "page_count",
        minWidth: 80,
        maxWidth: 120,
        isResizable: true
    },
    {
        key: "symbol_count",
        name: "Symbol Count",
        fieldName: "symbol_count",
        minWidth: 80,
        maxWidth: 120,
        isResizable: true
    }
];

const MetricCard: React.FC<MetricCardProps> = ({ title, value, subtitle, icon }) => (
    <div className={syncLogStyles.metricCard}>
        <div className={syncLogStyles.metricIcon}>{icon || "📊"}</div>
        <div className={syncLogStyles.metricContent}>
            <Text variant="small" className={syncLogStyles.metricTitle}>
                {title}
            </Text>
            <Text variant="xxLarge" className={syncLogStyles.metricValue}>
                {value}
            </Text>
            {subtitle && (
                <Text variant="tiny" className={syncLogStyles.metricSubtitle}>
                    {subtitle}
                </Text>
            )}
        </div>
    </div>
);

const TableSyncLog: React.FC<TableSyncLogProps> = ({ data }) => {
    const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());
    const [totalProcessedPages, setTotalProcessedPages] = useState<number>(0);
    const [isAllCollapsed, setIsAllCollapsed] = useState<boolean>(false);


    const metrics = useMemo(() => {
        if (!data.length) return null;

        const totalDocuments = data.length;
        const uniqueFileIds  = new Set(data.map(item => item.fid)).size;
        const totalPages     = data.map(item => item.page_count).reduce((sum, count) => sum + (count || 0), 0);

        const avgPagesPerDoc = totalPages / totalDocuments;

        if (totalPages !== totalProcessedPages) setTotalProcessedPages(totalPages);

        // calculate documents per day
        const dates       = data.map(item => new Date(item.stamp).toDateString());
        const uniqueDates = new Set(dates).size;
        const docsPerDay  = totalDocuments / (uniqueDates || 1);

        return {
            totalDocuments,
            uniqueFileIds,
            totalPages,
            avgPagesPerDoc: avgPagesPerDoc.toFixed(2),
            docsPerDay: docsPerDay.toFixed(2),
            uniqueDates: uniqueDates
        };
    }, [data]);

    const columns: IColumn[] = useMemo(() => {
        return baseColumns;
    }, []);

    const { sortedData, groups } = useMemo(() => {
        // sort by date (most recent first) for grouping
        const dataForGrouping = [...data].sort((a, b) => new Date(b.stamp).getTime() - new Date(a.stamp).getTime());

        // group by day
        const groupMap = new Map<string, gSyncLogItem[]>();
        dataForGrouping.forEach(item => {
            const dateKey = new Date(item.stamp).toLocaleDateString('UTC', { year: 'numeric', month: 'short', day: 'numeric' });
            if (!groupMap.has(dateKey)) groupMap.set(dateKey, []);

            groupMap.get(dateKey)!.push(item);
        });

        // create groups and flattened sorted data
        const groupedData: gSyncLogItem[] = [];
        const groupsList: IGroup[]        = [];
        let startIndex                    = 0;

        groupMap.forEach((items, dateKey) => {
            let numberOfGroupsForTheDate = new Set(items.map(i => i.groupname)).size;
            groupsList.push({
                key: dateKey,
                name: `${dateKey}`,
                startIndex: startIndex,
                count: items.length,
                level: 0,
                isCollapsed: collapsedGroups.has(dateKey),
                data: numberOfGroupsForTheDate
            });
            groupedData.push(...items);
            startIndex += items.length;
        });

        return { sortedData: groupedData, groups: groupsList };
    }, [data, collapsedGroups]);

    const toggleGroupCollapse = (group: IGroup, isCollapsed: boolean): void => {
        setCollapsedGroups(prev => {
            const newSet = new Set(prev);
            if (newSet.has(group.key)) {
                newSet.delete(group.key);
            } else {
                newSet.add(group.key);
            }
            return newSet;
        });
    };

    const onToggleCollapseAll = (isAllCollapsedNew: boolean): void => {
        if (isAllCollapsedNew === isAllCollapsed) return; // no change
        setIsAllCollapsed(isAllCollapsedNew);
        if (isAllCollapsedNew) {    // collapse all - add all group keys to set
            const allKeys = groups.map(g => g.key);
            setCollapsedGroups(new Set(allKeys));
        } else {    // expand all - clear set
            setCollapsedGroups(new Set());
        }
    };

    const onRenderGroupHeader = (props?: IDetailsGroupDividerProps ): JSX.Element | null => {
        if (!props || !props.group) return null;

        const { group }   = props;
        const isCollapsed = collapsedGroups.has(group.key);

        return (
            <div
                onClick={() => toggleGroupCollapse(group, isCollapsed)}
                style={{
                    padding: '8px 12px',
                    background: 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)',
                    borderBottom: '1px solid #e1e4e8',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    fontWeight: 600,
                    fontSize: '13px',
                    color: '#323130',
                    userSelect: 'none',
                    transition: 'background 0.2s ease',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = 'linear-gradient(135deg, #e8eaed 0%, #f8f9fa 100%)'}}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)' }}
            >
                <span style={{ marginRight: '8px', fontSize: '14px' }}>
                    {isCollapsed ? '▸' : '▾'}
                </span>
                <span>{group.name} ({group.count} items) {group.data > 1 ? `(${group.data} operators)` : ''}</span>
            </div>
        );
    };

    if (!data.length) return <div className={styles.noDataMessage}>No sync log data available.</div>;
    return (
        <div style={{ width: "100vw" }}>
            {/* metrics cards row */}
            <div style={{ width: "90vw", textAlign: "center", marginLeft:"auto", marginRight: "auto", display: "flex", justifyContent: "center" }}>
                {metrics && (
                    <div className={syncLogStyles.metricsContainer}>
                        <MetricCard
                            title="Total Processsed"
                            value={metrics.totalDocuments}
                            icon="📄"
                            subtitle="processing operations"
                        />
                        <MetricCard
                            title="Total Files"
                            value={metrics.uniqueFileIds}
                            icon="🗂️"
                            subtitle="distinct files"
                        />
                        <MetricCard
                            title="Total Pages"
                            value={metrics.totalPages}
                            icon="📑"
                            subtitle="processed pages"
                        />
                        <MetricCard
                            title="Pages/Doc"
                            value={metrics.avgPagesPerDoc}
                            icon="📊"
                            subtitle="average pages per document"
                        />
                        <MetricCard
                            title="Docs/Day"
                            value={metrics.docsPerDay}
                            icon="📈"
                            subtitle={`daily average for ${metrics.uniqueDates} active days`}
                        />
                    </div>
                )}
            </div>
            <div style={{ width: "88vw" }}>
                {/* data table */}
                <div style={{ height: "calc((100vh - 30vh) - 200px)" }} key={totalProcessedPages}> {/* force re-render on collapse change */}
                    <DetailsList
                        className={styles.detailsList}
                        items={sortedData}
                        columns={columns}
                        groups={groups}
                        groupProps={{
                            showEmptyGroups: false,
                            onRenderHeader: onRenderGroupHeader,
                            onToggleCollapseAll: onToggleCollapseAll
                        }}
                        setKey="set"
                        styles={controlStylesAnalyticsConvoList}
                        layoutMode={DetailsListLayoutMode.justified}
                        selectionMode={0}
                    />
                </div>
            </div>
        </div>

    );
};

export default TableSyncLog;
